import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, User, Users, Shield } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API = `${window.location.origin}/api`;
const STATIC_LOGO = '/assets/wg-navbar.png';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Consulting', path: '/consulting' },
  { name: 'Design', path: '/design' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Blog', path: '/blog' },
  { name: 'About', path: '/about' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(STATIC_LOGO);
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/site-settings/navbar-logo`)
      .then((res) => {
        if (!cancelled && res.data?.logo_url) {
          setLogoUrl(res.data.logo_url);
        }
      })
      .catch(() => {
        // Stay on the static fallback if the endpoint fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass py-3' : 'bg-transparent py-4'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex items-center justify-between gap-4">
            {/* Logo — admin-managed via Brand Assets (Settings → Set as Navbar Logo) */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
              <img
                src={logoUrl}
                alt="White Glove Event Concierge"
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs xl:text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA & Auth */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User size={18} />
                      )}
                      <span className="text-sm">{user?.name?.split(' ')[0]}</span>
                      <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0a0a0a] border-white/10 min-w-[180px]">
                    <DropdownMenuItem asChild>
                      <Link to="/portal" className="text-white/70 hover:text-white cursor-pointer" data-testid="portal-link">
                        Client Portal
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="text-white/70 hover:text-white cursor-pointer" data-testid="admin-link">
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      onClick={logout}
                      className="text-white/70 hover:text-white cursor-pointer"
                      data-testid="logout-btn"
                    >
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] py-2.5 px-5 hover:bg-[#D4AF37]/10 transition-colors"
                      data-testid="signin-dropdown-trigger"
                    >
                      Client Login
                      <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0a0a0a] border-white/10 min-w-[200px]">
                    <DropdownMenuItem asChild>
                      <Link to="/login" className="text-white/80 hover:text-white cursor-pointer flex items-center gap-2" data-testid="client-signin-link">
                        <Users size={14} className="text-[#D4AF37]" />
                        Client Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem asChild>
                      <Link to="/login?admin=1" className="text-white/80 hover:text-white cursor-pointer flex items-center gap-2" data-testid="admin-signin-link">
                        <Shield size={14} className="text-[#D4AF37]" />
                        Admin Sign In
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Link
                to="/contact"
                className="bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.2em] py-2.5 px-5 hover:bg-[#C9A432] transition-colors font-medium"
                data-testid="contact-btn"
              >
                Inquire Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#050505] pt-24 px-6 lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-5">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Link
                    to={link.path}
                    className={`text-2xl font-heading ${
                      location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-6 border-t border-white/10 flex flex-col gap-3">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/portal"
                      className="btn-secondary text-center"
                      data-testid="mobile-portal-link"
                    >
                      Client Portal
                    </Link>
                    <button
                      onClick={logout}
                      className="text-white/70 text-center py-3"
                      data-testid="mobile-logout-btn"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="btn-secondary text-center"
                      data-testid="mobile-client-signin"
                    >
                      Client Sign In
                    </Link>
                    <Link
                      to="/login?admin=1"
                      className="text-white/70 text-center py-3 border border-white/10 text-xs uppercase tracking-widest"
                      data-testid="mobile-admin-signin"
                    >
                      Admin Sign In
                    </Link>
                  </>
                )}
                <Link
                  to="/contact"
                  className="bg-[#D4AF37] text-black text-center py-3 text-sm uppercase tracking-widest font-medium"
                  data-testid="mobile-contact-btn"
                >
                  Inquire Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
